import {api, DateRange, isPaginatedQuery, PaginationQuery, PaginationResponse} from '@services/Api'
import {DEFAULT_POSTS_PER_PAGE, ImageUknown} from "@lib/constants";
import {removeEncodedChars} from "@lib/format";
import {transformParamsToQuerystring} from "@lib/utils";

export interface Issue {
  id: number;
  series_id: number;
  series_title: string;
  series_slug: string;
  series_years: string | null;
  publisher_id: number;
  publisher: string;
  slug: string;
  title: string;
  date: Date,
  issue_number: string;
  cover_price: number;
  graded_or_raw: string;
  grade: string;
  most_common_grade: string;
  is_most_common_value: boolean;
  value: number;
  user_value: number | null;
  trend_percent: number | null;
  is_variant: boolean;
  variant_of: number | null;
  variant_name: string;
  variant_count: number | null;
  collection_count: number | null;
  volume_name: string | null;
  is_rare: boolean;
  key_info?: string;
  is_key: boolean;
  is_hot: boolean;
  full_image: string;
  medium_image: string;
  in_wishlist: boolean;
  total_in_collection: number;
  total_in_grade: number;
  purchase_price: number | null;
  total_comic_cost: number | null;
  purchase_date: string | null;
  collection_item_id: number | null;
  story_rating: number | null;
  art_rating: number | null;
  overall_rating: number | null;
  recommend: number | null;
  first_issue_id: number;
  country_slug: string;
  format: string;
  user_growth: number | null;
  user_profit: number | null;
  notes: string | null;
  box_ids: string | null;
  signed_by_ids: string | null;
  review_internal_art: number | null;
  review_review: string | null;
  label_type: number | null;
  cert_number: string | null;
  page_quality: number | null;
  get_it_slabbed: boolean | null;
  slab_quality: number | null;

  grader_notes: string | null;
  purchase_id: number | null;
  purchase_trx_id: string | null;
  purchase_contact_id: number | null;
  purchase_fees: number | null;
  purchase_additional_expenses: number | null;
  purchase_venue: string | null;
  purchase_notes: string | null;
  purchase_shipping_co: string | null;
  purchase_shipping_fees: number | null;
  purchase_tracking_number: string | null;
  purchase_contact_rating: number | null;
  purchase_contact_notes: string | null;
  buyer_contact_name: string | null;
  buyer_store_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  buyer_address1: string | null;
  buyer_address2: string | null;
  buyer_address3: string | null;
  buyer_address4: string | null;
  buyer_city: string | null;
  buyer_state: string | null;
  buyer_zip: string | null;
  custom_label: string | null;

  wishlist_id: number | null;
  wishlist_qty: number | null;
  stale: boolean;

  sales_list_data?: any;

  raw_fmv_grade: string | null,
  raw_fmv_value: number | null;
  raw_fmv_stale: boolean | null,
  slabbed_fmv_grade: string | null,
  slabbed_fmv_value: number | null;
  slabbed_fmv_stale: boolean | null,

  issue_notes: string | null;

  paired_grade: string | null,
  paired_value: number | null;
  paired_stale: boolean | null,
}

export interface Publisher {
  id: number;
  title: string;
  count: number;
}

export interface Era {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
}

export interface SeriesType {
  title: string;
  count: number;
}

export interface ElasticSearchBucketResponse {
  key: string;
  doc_count: number;
}

export interface IssuesResponse {
  issues: Issue[];
  overview: {
    total: number;
    is_key: number;
    is_hot: number;
    is_rare: number;
    publishers?: ElasticSearchBucketResponse[];
    series_types?: ElasticSearchBucketResponse[];
  };
  pagination?: PaginationResponse;
  date?: Date;
  limit?: number;
  limit_hit?: boolean;
}

interface FilterOptionsResponse {
  publishers: Publisher[];
  eras: Era[];
  totalIssues: number;
  total_raw?: number;
  total_slabbed?: number;
}

export interface IssueFilters {
  date?: DateRange,
  multipleDateRanges?: DateRange[];
  showVariants?: boolean;
  onlyVariants?: boolean;
  onlyFirstIssues?: boolean;
  onlyAllTimeHighSale?: boolean;
  isHot?: boolean;
  isKey?: boolean;
  isRare?: boolean;
  isTopVariant?: boolean;
  query?: string;
  subquery?: string;
  minimumScore?: number;
  publisherId?: number;
  publishers?: number[];
  inCollection?: boolean;
  inWishlist?: boolean;

  series?: number[];
  sort?: number | string;
  groupByGrade?: boolean;
  groupByIssue?: boolean;
  locations?: string[];

  boxes?: number[];
  gradedBy?: string[];
  grades?: string[];
  formats?: string[];
  issueId?: number;
  issueIds?: number[];
  itemInCollectionId?: string | number;
  variantOf?: number;
  showValuesShown?: number; // 0 = all, 1 = CovrPrice est value, 2 = user value.
  getItSlabbed?: boolean;
  isSigned?: boolean;

  hideSeriesFromNewReleases?: boolean;
  hideRaw?: boolean; // Only for shakers list
  hideSlabbed?: boolean; // Only for shakers list

  descriptorId?: number;
  barcode?: string;
}

export interface IssueFiltersQuery {
  filters: IssueFilters,
  listType?: string,
  targetDate?: string,
}

export interface IssueFiltersPaginatedQuery extends IssueFiltersQuery, PaginationQuery { }

export interface SalesListFiltersQuery {
    filters: IssueFilters,
    targetDate: string,
    listType: string,
}

export interface SalesListFiltersPaginatedQuery extends SalesListFiltersQuery, PaginationQuery { }

export interface SalesListResponse extends IssuesResponse {
  article_url?: string;
}

export const transformSingleIssueResponse = (issue: any) => {
  return ({
    ...issue,
    id: Number(issue.id),
    collection_item_id: issue.collection_item_id ? Number(issue.collection_item_id) : null,
    series_id: Number(issue.series_id),
    title: removeEncodedChars(issue.title),
    series_title: removeEncodedChars(issue.series_title),
    variant_name: removeEncodedChars(issue.variant_name),
    purchase_price: issue.purchase_price ? Number(issue.purchase_price) : null,
    total_comic_cost: issue.total_comic_cost ? Number(issue.total_comic_cost) : null,
    purchase_date: issue.purchase_date ? issue.purchase_date : null,
    publisher_id: Number(issue.publisher_id),
    issue_number: issue.issue_number && issue.issue_number !== '[nn]' ? `#${issue.issue_number}` : '',
    cover_price: Number(issue.cover_price),
    value: Number(issue.value),
    user_value: issue.user_value ? Number(issue.user_value) : null,
    trend_percent: issue.trend_percent ? Number(issue.trend_percent) : null,
    is_variant: (String(issue.is_variant) === '1'),
    variant_of: issue.variant_of && String(issue.variant_of) !== '0' ? Number(issue.variant_of) : null,
    key_info: removeEncodedChars(issue.key_info),
    is_rare: (String(issue.is_rare) === '1'),
    is_key: (String(issue.is_key) === '1'),
    is_hot: (String(issue.is_hot) === '1'),
    in_wishlist: (String(issue.in_wishlist) === '1'),
    total_in_collection: issue.total_in_collection && String(issue.total_in_collection) !== '0' ? Number(issue.total_in_collection) : 0,
    total_in_grade: issue.total_in_grade && String(issue.total_in_grade) !== '0' ? Number(issue.total_in_grade) : 0,
    story_rating: issue.story_rating ? Number(issue.story_rating) : null,
    art_rating: issue.art_rating ? Number(issue.art_rating) : null,
    overall_rating: issue.overall_rating ? Number(issue.overall_rating) : null,
    recommend: issue.recommend ? Number(issue.recommend) : null,
    user_growth: issue.user_growth ? Number(issue.user_growth) : null,
    user_profit: issue.user_profit ? Number(issue.user_profit) : null,
    notes: issue.notes,
    box_ids: issue.box_ids,
    signed_by_ids: issue.signed_by_ids,
    review_internal_art: issue.review_internal_art ? Number(issue.review_internal_art) : null,
    review_review: issue.review_review,
    label_type: issue.label_type ? Number(issue.label_type) : null,
    cert_number: issue.cert_number,
    page_quality: issue.page_quality ? Number(issue.page_quality) : null,
    get_it_slabbed: (String(issue.get_it_slabbed) === '1'),
    slab_quality: issue.slab_quality ? Number(issue.slab_quality) : null,
    grader_notes: issue.grader_notes,
    purchase_id: issue.purchase_id ? Number(issue.purchase_id) : null,
    purchase_trx_id: issue.purchase_trx_id,
    purchase_contact_id: issue.purchase_contact_id,
    purchase_fees: issue.purchase_fees ? Number(issue.purchase_fees) : null,
    purchase_additional_expenses: issue.purchase_additional_expenses ? Number(issue.purchase_additional_expenses) : null,
    purchase_venue: issue.purchase_venue,
    purchase_notes: issue.purchase_notes,
    purchase_shipping_co: issue.purchase_shipping_co,
    purchase_shipping_fees: issue.purchase_shipping_fees ? Number(issue.purchase_shipping_fees) : null,
    purchase_tracking_number: issue.purchase_tracking_number,
    purchase_contact_rating: issue.purchase_contact_rating ? Number(issue.purchase_contact_rating) : null,
    purchase_contact_notes: issue.purchase_contact_notes,
    buyer_contact_name: issue.buyer_contact_name,
    buyer_store_name: issue.buyer_store_name,
    buyer_email: issue.buyer_email,
    buyer_phone: issue.buyer_phone,
    buyer_address1: issue.buyer_address1,
    buyer_address2: issue.buyer_address2,
    buyer_address3: issue.buyer_address3,
    buyer_address4: issue.buyer_address4,
    buyer_city: issue.buyer_city,
    buyer_state: issue.buyer_state,
    buyer_zip: issue.buyer_zip,
    custom_label: issue.custom_label,
    wishlist_id: issue.wishlist_id ? Number(issue.wishlist_id) : null,
    wishlist_qty: issue.wishlist_qty ? Number(issue.wishlist_qty) : null,
    is_most_common_value: (String(issue.is_most_common_value) === '1'),
    stale: (String(issue.stale) === '1'),
    location: issue.location ? issue.location !== '' ? issue.location : null : null,
    sales_list_data: issue.sales_list_data ? transformSalesListData(issue.sales_list_data) : null,

    raw_fmv_grade: issue.raw_fmv_grade,
    raw_fmv_value: issue.raw_fmv_value ? Number(issue.raw_fmv_value) : null,
    raw_fmv_stale: (String(issue.raw_fmv_stale) === '1'),
    slabbed_fmv_grade: issue.slabbed_fmv_grade,
    slabbed_fmv_value: issue.slabbed_fmv_value ? Number(issue.slabbed_fmv_value) : null,
    slabbed_fmv_stale: (String(issue.raw_fmv_stale) === '1'),
    paired_grade: issue.paired_grade,
    paired_value: issue.paired_value ? Number(issue.paired_value) : null,
    paired_stale: (String(issue.paired_stale) === '1'),
  })
};

export const transformSalesListData = (data: any) => {
    return {
      ...data,
      id: Number(data.id),
      sort1: Number(data.sort1),
      sort2: Number(data.sort1),
      issue_id:  Number(data.issue_id),
      grades_hit: data.grades_hit ? Number(data.grades_hit) : null,
      total_sales_count: data.total_sales_count ? Number(data.total_sales_count) : null,
      total_sales_spend: data.total_sales_spend ? Number(data.total_sales_spend) : null,
      total_sales_avg: data.total_sales_avg ? Number(data.total_sales_avg) : null,
      in_grade_sales_count: data.in_grade_sales_count ? Number(data.in_grade_sales_count) : null,
      in_grade_graded: data.in_grade_graded ? Number(data.in_grade_graded) : null,
      graded_or_raw: data.in_grade_graded === '1' ? 'CGC' : 'RAW',
      in_grade_sales_avg: data.in_grade_sales_avg ? Number(data.in_grade_sales_avg) : null,
      in_grade_recent_avg: data.in_grade_recent_avg ? Number(data.in_grade_recent_avg) : null,
      in_grade_ratio: data.in_grade_ratio ? Number(data.in_grade_ratio) : null,
      in_grade_high_sale: data.in_grade_high_sale ? Number(data.in_grade_high_sale) : null,
      in_grade_historic_high_sale: data.in_grade_historic_high_sale ? Number(data.in_grade_historic_high_sale) : null,
      in_grade_historic_high_ratio: data.in_grade_historic_high_ratio ? Number(data.in_grade_historic_high_ratio) : null,
      history_sales_count: data.history_sales_count ? Number(data.history_sales_count) : null,
      history_growth_ratio: data.history_growth_ratio ? Number(data.history_growth_ratio) : null,
      history_sales_spend: data.history_sales_spend ? Number(data.history_sales_spend) : null,
      is_all_time_high: (String(data.is_all_time_high) === '1'),
      top_sale_price: data.top_sale_price ? Number(data.top_sale_price) : null,
      top_sale_graded: data.top_sale_graded ? Number(data.top_sale_graded) : null,
      top_sale_graded_or_raw: data.top_sale_graded ? data.top_sale_graded === '1' ? 'CGC' : 'RAW' : null,
    }
}

export const transformSingleESIssueResponse = (issue: any) => {
  return ({
    ...issue,
    id: Number(issue._id),
    slug: issue._source.post_name,
    series_id: Number(issue._source.series_id),
    series_title: issue._source.series_title,
    series_years: issue._source.series_years,
    publisher_id: Number(issue._source.publisher_id),
    publisher: issue._source.publisher_title,
    issue_number: issue._source.issue_number && issue._source.issue_number !== '[nn]' ? `#${issue._source.issue_number}` : '',
    cover_price: Number(issue._source.cover_price),
    value: issue._source.most_common_sale_value ? Number(issue._source.most_common_sale_value) : null,
    trend_percent: issue._source.trend_percent ? Number(issue._source.trend_percent) : null,
    is_variant: issue._source.is_variant,
    variant_of: issue._source.variant_of && String(issue._source.variant_of) !== '0' ? Number(issue._source.variant_of) : null,
    is_rare: issue._source.is_rare_comic,
    is_key: issue._source.is_key_comic,
    is_hot: issue._source.is_hot_comic,
    in_wishlist: issue.in_wishlist == '1' ? true : false,
    total_in_collection: issue.total_in_collection ? issue.total_in_collection : 0,
    total_in_grade: 0,
    is_most_common_value: false,
    stale: false,
    full_image: issue._source.full_image && issue._source.full_image.length >= 0 ? issue._source.full_image : ImageUknown.COVER,
    graded_or_raw: issue._source.most_common_graded_or_raw,
    format: issue._source.first_issue_format_slug,
    variant_count: issue._source.variant_count ? Number(issue._source.variant_count) : null,
    collection_count: issue._source.collection_count ? Number(issue._source.collection_count) : null,
    volume_name: issue._source.volume_name,
    date: issue._source.post_date,
    variant_name: issue._source.variant_name,
    most_common_grade: issue._source.most_common_grade,
    key_info: removeEncodedChars(issue._source.key_comic_reason),
  })
};

export const transformIssueResponse = (responseData: any) => {
  return {
    ...responseData, issues: responseData.issues.map(issue => transformSingleIssueResponse(issue))
  }
};

export const transformESIssueResponse = (responseData: IssuesResponse) => {
  return {
    ...responseData, issues: responseData.issues.map(issue => transformSingleESIssueResponse(issue))
  }
};

const issueApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIssuesViaElasticSearch: build.query<IssuesResponse, IssueFiltersQuery | IssueFiltersPaginatedQuery>({
      query: (args) => {
        // There's an issue with btoa against unicode characters, this is the recommended work around.
        // https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
        const textEncodedFilters = new TextEncoder().encode(JSON.stringify(args.filters));
        const binString = Array.from(textEncodedFilters, (x) => String.fromCodePoint(x)).join("");
        const filters = args ? btoa(encodeURIComponent(binString)) : '';

        if (isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/search/issues?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/search/issues?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Issue'],
      transformResponse: transformIssueResponse,
    }),
    getIssues: build.query<IssuesResponse, IssueFiltersQuery | IssueFiltersPaginatedQuery>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';

        if (isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/issues?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/issues?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Issue'],
      transformResponse: transformIssueResponse,
    }),
    getFilterOptions: build.query<FilterOptionsResponse, IssueFiltersQuery>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';
        const listType = args.listType ? args.listType : null;
        const targetDate = args.targetDate ? args.targetDate : null;

        return {
          url: `covr/v1/issues/filter-options?filters=${filters}` + (listType ? `&list_type=${listType}` : '') + (targetDate ? `&target_date=${targetDate}` : ''),
          method: "GET",
        };
      },
      providesTags: ['Publisher'],
    }),
    getNewReleaseWeek: build.query<string, null>({
      query: () => {
        return {
          url: `covr/v1/newreleases/week`,
          method: "GET",
        };
      }
    }),
    getIssueCoverArt: build.query({
      query: (params) => {
        const queryParams = transformParamsToQuerystring(params);
        return {
          url: `/covr/v1/issues/coverart/?${queryParams}`,
          method: "GET",
        };
      },
      providesTags: [],
    }),
    getIssueWeeklyTopTen: build.query({
      query: (params) => {
        const queryParams = transformParamsToQuerystring(params);
        return {
          url: `/covr/v1/issues/weekly-top-ten/?${queryParams}`,
          method: "GET",
        };
      },
      providesTags: ['Issue'],
    }),
    getIssuesHomepageNewReleases: build.query({
      query: (params) => {
        return {
          url: `/covr/v1/issues/home-page/new-releases`,
          method: "GET",
        };
      },
      providesTags: [],
      transformResponse: transformIssueResponse,
    }),
    getIssuesHomepageMarketplace: build.query({
      query: (params) => {
        return {
          url: `/covr/v1/issues/home-page/marketplace`,
          method: "GET",
        };
      },
      providesTags: [],
      transformResponse: transformIssueResponse,
    }),
    removeLocationFromCollection: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/remove_location`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue'],
    }),
    getIssueEras: build.query<any, null>({
      query: () => {
        return {
          url: `/covr/v1/issues/eras`,
          method: "GET",
        };
      },
    }),
    getIssuesSalesList: build.query<SalesListResponse, SalesListFiltersQuery | SalesListFiltersPaginatedQuery>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';
        const targetDate = args.targetDate ? args.targetDate : null;
        const listType = args.listType ? args.listType : null;

        if (isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/issues/sales_list?filters=${filters}&list_type=${listType}&page=${page}&per_page=${perPage}`+ (targetDate ? `&target_date=${targetDate}` : ''),
            method: "GET",
          };
        }
        return {
          url: `covr/v1/issues/sales_list?filters=${filters}&list_type=${listType}`+ (targetDate ? `&target_date=${targetDate}` : ''),
          method: "GET",
        };
      },
      providesTags: ['Issue'],
      transformResponse: transformIssueResponse,
    }),
    getValuesByIssueId: build.query<any, any>({
      query: ({issueId}) => {
        return {
          url: `covr/v1/issue/values/by_id?issue_id=${issueId}`,
          method: "GET",
        };
      },
    }),
  })
});

export const {
  useGetIssuesViaElasticSearchQuery,
  useGetIssuesQuery,
  useGetIssueCoverArtQuery,
  useGetFilterOptionsQuery,
  useGetIssueWeeklyTopTenQuery,
  useGetNewReleaseWeekQuery,
  useRemoveLocationFromCollectionMutation,
  useGetIssueErasQuery,
  useGetIssuesHomepageNewReleasesQuery,
  useGetIssuesHomepageMarketplaceQuery,
  useGetIssuesSalesListQuery,
  useGetValuesByIssueIdQuery,
} = issueApi;
